import React, { useState, useEffect, useCallback } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area 
} from 'recharts';

const Dashboard = () => {
  // State Management
  const [portfolioData, setPortfolioData] = useState({
    totalEquity: 1170,
    dailyPL: 0.71,
    dailyPLDollar: 8.31,
    winRate30D: 65,
    exposurePercent: 23.5,
    availableCash: 895.50,
    totalPositions: 3
  });

  const [alerts, setAlerts] = useState([
    { type: 'warning', message: 'Drawdown > 5%, Auto-Scaling Risk Down', active: true },
    { type: 'success', message: 'IBKR Connection Active', active: true },
    { type: 'success', message: 'Government APIs Online', active: true }
  ]);

  const [marketData, setMarketData] = useState({
    SPY: { price: 418.75, change: 0.56 },
    QQQ: { price: 325.81, change: 0.94 },
    IWM: { price: 176.98, change: 0.43 },
    VIX: { price: 13.42, change: -2.1 }
  });

  const [sentimentData, setSentimentData] = useState({
    fearGreed: 52,
    fearGreedText: 'Neutral',
    regime: 'Risk-On / Volatility Low',
    aiInsight: 'Today: Favoring long setups in Tech & Small Caps, volatility low'
  });

  // WORKING NEWS HEADLINES with REAL LINKS
  const [newsHeadlines, setNewsHeadlines] = useState([
    {
      id: 1,
      headline: 'Fed Signals Potential Rate Cut in September',
      source: 'Reuters',
      time: '2 hours ago',
      category: 'Economy',
      url: 'https://www.reuters.com/markets/us/',
      impact: 'high'
    },
    {
      id: 2,
      headline: 'Tech Stocks Rally on AI Chip Breakthrough',
      source: 'CNBC',
      time: '4 hours ago',
      category: 'Technology',
      url: 'https://www.cnbc.com/technology/',
      impact: 'medium'
    },
    {
      id: 3,
      headline: 'Defense Spending Bill Passes Senate',
      source: 'Bloomberg',
      time: '6 hours ago',
      category: 'Defense',
      url: 'https://www.bloomberg.com/markets',
      impact: 'medium'
    },
    {
      id: 4,
      headline: 'Oil Prices Surge on Middle East Tensions',
      source: 'Wall Street Journal',
      time: '8 hours ago',
      category: 'Energy',
      url: 'https://www.wsj.com/markets',
      impact: 'high'
    }
  ]);

  const [openPositions, setOpenPositions] = useState([
    { 
      ticker: 'TSLA', 
      shares: 50, 
      entry: 237.60, 
      current: 235.85, 
      pnl: -87.50, 
      pnlPercent: -0.74,
      aiScore: 7.2,
      strategy: 'Options Core',
      stopLoss: 225.50,
      target: 255.00,
      daysHeld: 2
    },
    { 
      ticker: 'LMT', 
      shares: 75, 
      entry: 410.25, 
      current: 422.10, 
      pnl: 888.75, 
      pnlPercent: 2.89,
      aiScore: 9.1,
      strategy: 'Government Intel',
      stopLoss: 395.00,
      target: 450.00,
      daysHeld: 1
    },
    { 
      ticker: 'NVDA', 
      shares: 25, 
      entry: 445.80, 
      current: 448.25, 
      pnl: 61.25, 
      pnlPercent: 0.55,
      aiScore: 8.3,
      strategy: 'Phoenix Scalp',
      stopLoss: 432.50,
      target: 465.00,
      daysHeld: 0
    }
  ]);

  const monthlyPnL = [
    { month: 'Jan', pnl: 45.20, cumulative: 45.20 },
    { month: 'Feb', pnl: 67.80, cumulative: 113.00 },
    { month: 'Mar', pnl: 89.50, cumulative: 202.50 },
    { month: 'Apr', pnl: 23.10, cumulative: 225.60 },
    { month: 'May', pnl: 156.30, cumulative: 381.90 },
    { month: 'Jun', pnl: 98.70, cumulative: 480.60 },
    { month: 'Jul', pnl: 178.20, cumulative: 658.80 },
    { month: 'Aug', pnl: 125.40, cumulative: 784.20 }
  ];

  const profitByRegion = [
    { name: 'US Markets', value: 70, color: '#D4AF37' },
    { name: 'European', value: 20, color: '#0069B1' },
    { name: 'Asian', value: 10, color: '#5BC0DE' }
  ];

  const upcomingCatalysts = [
    { date: 'Aug 3', event: 'FOMC Meeting', impact: 'high', sector: 'Broad Market' },
    { date: 'Aug 5', event: 'TSLA Earnings', impact: 'medium', sector: 'Tech' },
    { date: 'Aug 7', event: 'Jobs Report', impact: 'high', sector: 'Broad Market' },
    { date: 'Aug 9', event: 'CPI Data', impact: 'high', sector: 'Financials' }
  ];

  // API Connection Functions
  const refreshAllData = useCallback(async () => {
    try {
      const ibkrResponse = await fetch('/api/ibkr/portfolio');
      const govResponse = await fetch('/api/government/alerts');
      const marketResponse = await fetch('/api/market/current');
      
      console.log('Data refreshed successfully');
      setAlerts(prev => [...prev, { type: 'success', message: 'Data refreshed', active: true }]);
    } catch (error) {
      console.error('Data refresh failed:', error);
      setAlerts(prev => [...prev, { type: 'danger', message: 'Refresh failed', active: true }]);
    }
  }, []);

  const triggerKillSwitch = useCallback(async () => {
    try {
      await fetch('/api/risk/kill-switch', { method: 'POST' });
      setAlerts(prev => [...prev, { type: 'danger', message: 'KILL SWITCH ACTIVATED', active: true }]);
    } catch (error) {
      console.error('Kill switch failed:', error);
    }
  }, []);

  const exportReport = useCallback(() => {
    const reportData = {
      portfolio: portfolioData,
      positions: openPositions,
      performance: monthlyPnL,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fastwater-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [portfolioData, openPositions, monthlyPnL]);

  // Helper Functions
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);

  const formatPercent = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

  const getNewsIcon = (category) => {
    switch(category) {
      case 'Economy': return '📊';
      case 'Technology': return '💻';
      case 'Defense': return '🛡️';
      case 'Energy': return '⚡';
      default: return '📰';
    }
  };

  const getImpactColor = (impact) => {
    switch(impact) {
      case 'high': return 'text-loss';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-white';
    }
  };

  const getAIScoreColor = (score) => {
    if (score >= 8.5) return '#00FF5F';
    if (score >= 7.0) return '#FFB800';
    return '#FF0000';
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      backgroundColor: '#1a2332',
      color: '#ffffff',
      fontFamily: 'Georgia, serif'
    }}>
      {/* REAL WATERMARK FROM PUBLIC FOLDER */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: '0.05',
        pointerEvents: 'none',
        zIndex: '1',
        width: '600px',
        height: '600px'
      }}>
        <img 
          src="/finance-watermark.jpg"
          alt="Global Trading Watermark"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* Content Layer */}
      <div style={{ position: 'relative', zIndex: '2' }}>
        {/* Alert Strip - Notre Dame Gold */}
        <div style={{
          backgroundColor: '#D4AF37',
          color: '#202A44',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'Georgia, serif'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px'
          }}>
            {alerts.filter(alert => alert.active).map((alert, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                <span style={{ fontWeight: '600' }}>{alert.message}</span>
              </div>
            ))}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button 
              onClick={refreshAllData}
              style={{
                backgroundColor: '#0069B1',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Georgia, serif',
                fontWeight: '600'
              }}
            >
              <span>🔄</span>
              <span>Refresh</span>
            </button>
            <button 
              onClick={triggerKillSwitch}
              style={{
                backgroundColor: '#EF4444',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Georgia, serif',
                fontWeight: '600'
              }}
            >
              <span>🛑</span>
              <span>Kill Switch</span>
            </button>
            <button 
              onClick={exportReport}
              style={{
                backgroundColor: '#0069B1',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'Georgia, serif',
                fontWeight: '600'
              }}
            >
              <span>📊</span>
              <span>Export</span>
            </button>
          </div>
        </div>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '24px'
        }}>
          {/* Header - Only Last Update */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '24px'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '14px',
                color: '#a0a9c1',
                fontFamily: 'Georgia, serif'
              }}>Last Update</div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                fontFamily: 'Georgia, serif'
              }}>{new Date().toLocaleTimeString()}</div>
            </div>
          </div>

          {/* Portfolio Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0',
                  fontFamily: 'Georgia, serif'
                }}>Total Equity</h3>
                <div style={{ width: '64px', height: '32px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[{v:1},{v:1.2},{v:1.1},{v:1.3},{v:1.25}]}>
                      <Line type="monotone" dataKey="v" stroke="#D4AF37" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#ffffff',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                {formatCurrency(portfolioData.totalEquity)}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#10B981',
                fontFamily: 'Georgia, serif'
              }}>
                +2.3% This Week
              </div>
            </div>

            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0',
                  fontFamily: 'Georgia, serif'
                }}>Daily P&L</h3>
                <div style={{ width: '64px', height: '32px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[{v:0.2},{v:0.5},{v:0.3},{v:0.8},{v:0.71}]}>
                      <Area type="monotone" dataKey="v" stroke="#00FF5F" fill="#00FF5F" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#00FF5F',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                {formatPercent(portfolioData.dailyPL)}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#00FF5F',
                fontFamily: 'Georgia, serif'
              }}>
                {formatCurrency(portfolioData.dailyPLDollar)}
              </div>
            </div>

            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0',
                  fontFamily: 'Georgia, serif'
                }}>Win Rate (30D)</h3>
                <div style={{ width: '64px', height: '32px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[{v:60},{v:65},{v:62},{v:68},{v:65}]}>
                      <Bar dataKey="v" fill="#0069B1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#ffffff',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                {portfolioData.winRate30D}%
              </div>
              <div style={{
                fontSize: '14px',
                color: '#0069B1',
                fontFamily: 'Georgia, serif'
              }}>
                Above Target (60%)
              </div>
            </div>

            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0',
                  fontFamily: 'Georgia, serif'
                }}>Exposure</h3>
                <div style={{ width: '64px', height: '32px' }}>
                  <div style={{
                    width: '100%',
                    backgroundColor: '#3a4a6b',
                    borderRadius: '4px',
                    height: '8px',
                    marginTop: '12px'
                  }}>
                    <div style={{
                      backgroundColor: '#ffb700ff',
                      height: '8px',
                      borderRadius: '4px',
                      width: `${portfolioData.exposurePercent}%`,
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#ffffff',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                {portfolioData.exposurePercent}%
              </div>
              <div style={{
                fontSize: '14px',
                color: '#FFB800',
                fontFamily: 'Georgia, serif'
              }}>
                Safe Level (&lt;30%)
              </div>
            </div>
          </div>

          {/* Market Context Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* Market Context */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                fontFamily: 'Georgia, serif'
              }}>Market Context</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px'
              }}>
                {Object.entries(marketData).map(([symbol, data]) => (
                  <div key={symbol} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <div style={{
                        color: '#ffffff',
                        fontWeight: '600',
                        fontFamily: 'Georgia, serif'
                      }}>{symbol}</div>
                      <div style={{
                        color: '#a0a9c1',
                        fontSize: '14px',
                        fontFamily: 'Georgia, serif'
                      }}>{data.price.toFixed(2)}</div>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: data.change > 0 ? '#00FF5F' : '#FF0000',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {formatPercent(data.change)}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                borderTop: '1px solid #3a4a6b',
                paddingTop: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    color: '#a0a9c1',
                    fontFamily: 'Georgia, serif'
                  }}>Fear & Greed</span>
                  <span style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontFamily: 'Georgia, serif'
                  }}>{sentimentData.fearGreedText}</span>
                </div>
                <div style={{
                  width: '100%',
                  backgroundColor: '#3a4a6b',
                  borderRadius: '4px',
                  height: '8px'
                }}>
                  <div style={{
                    backgroundColor: '#D4AF37',
                    height: '8px',
                    borderRadius: '4px',
                    width: `${sentimentData.fearGreed}%`,
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>
            </div>

            {/* Today's Regime */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                fontFamily: 'Georgia, serif'
              }}>Today's Regime</h3>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#00FF5F',
                  marginBottom: '8px',
                  fontFamily: 'Georgia, serif'
                }}>
                  {sentimentData.regime}
                </div>
                <div style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  marginBottom: '16px',
                  fontFamily: 'Georgia, serif'
                }}>
                  Market Classification
                </div>
                <div style={{
                  backgroundColor: '#2B3554',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    color: '#D4AF37',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '4px',
                    fontFamily: 'Georgia, serif'
                  }}>AI Insight</div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'Georgia, serif'
                  }}>
                    {sentimentData.aiInsight}
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Catalysts */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                fontFamily: 'Georgia, serif'
              }}>Upcoming Catalysts</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {upcomingCatalysts.map((catalyst, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '12px',
                    borderBottom: index < upcomingCatalysts.length - 1 ? '1px solid #3a4a6b' : 'none'
                  }}>
                    <div>
                      <div style={{
                        color: '#ffffff',
                        fontWeight: '500',
                        fontSize: '14px',
                        fontFamily: 'Georgia, serif'
                      }}>{catalyst.event}</div>
                      <div style={{
                        color: '#a0a9c1',
                        fontSize: '12px',
                        fontFamily: 'Georgia, serif'
                      }}>{catalyst.sector}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        color: '#D4AF37',
                        fontSize: '14px',
                        fontWeight: '600',
                        fontFamily: 'Georgia, serif'
                      }}>{catalyst.date}</div>
                      <div style={{
                        fontSize: '12px',
                        color: catalyst.impact === 'high' ? '#FF0000' : 
                              catalyst.impact === 'medium' ? '#FFB800' : '#10B981',
                        fontFamily: 'Georgia, serif'
                      }}>
                        {catalyst.impact.toUpperCase()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* NEWS HEADLINES & Open Positions */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* NEWS HEADLINES with WORKING LINKS */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  fontFamily: 'Georgia, serif'
                }}>📰 News Headlines</h3>
                <a 
                  href="https://www.google.com/news"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0069B1',
                    fontSize: '14px',
                    textDecoration: 'none',
                    fontFamily: 'Georgia, serif'
                  }}
                >
                  View All →
                </a>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '320px',
                overflowY: 'auto'
              }}>
                {newsHeadlines.map((news) => (
                  <a 
                    key={news.id} 
                    href={news.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: '12px',
                      backgroundColor: '#2B3554',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#3a4a6b';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#2B3554';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <div style={{ fontSize: '20px' }}>{getNewsIcon(news.category)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: '14px',
                          marginBottom: '4px',
                          lineHeight: '1.3',
                          fontFamily: 'Georgia, serif'
                        }}>
                          {news.headline}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{
                            color: '#a0a9c1',
                            fontSize: '12px',
                            fontFamily: 'Georgia, serif'
                          }}>
                            {news.source} • {news.time}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            color: news.impact === 'high' ? '#FF0000' : 
                                  news.impact === 'medium' ? '#FFB800' : '#10B981',
                            fontFamily: 'Georgia, serif'
                          }}>
                            {news.impact.toUpperCase()}
                          </div>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#0069B1',
                          marginTop: '4px',
                          fontFamily: 'Georgia, serif'
                        }}>
                          {news.category}
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Open Positions */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '24px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  fontFamily: 'Georgia, serif'
                }}>Open Positions</h3>
                <div style={{
                  color: '#a0a9c1',
                  fontSize: '14px',
                  fontFamily: 'Georgia, serif'
                }}>{openPositions.length} Active</div>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {openPositions.map((position, index) => (
                  <div key={index} style={{
                    padding: '16px',
                    backgroundColor: '#2B3554',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{
                          color: '#ffffff',
                          fontWeight: 'bold',
                          fontSize: '18px',
                          fontFamily: 'Georgia, serif'
                        }}>{position.ticker}</div>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: getAIScoreColor(position.aiScore)
                        }}></div>
                        <div style={{
                          color: '#a0a9c1',
                          fontSize: '14px',
                          fontFamily: 'Georgia, serif'
                        }}>AI: {position.aiScore}/10</div>
                      </div>
                      <div style={{
                        fontWeight: '600',
                        color: position.pnl > 0 ? '#00FF5F' : '#FF0000',
                        fontFamily: 'Georgia, serif'
                      }}>
                        {formatCurrency(position.pnl)}
                      </div>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '8px',
                      fontSize: '14px'
                    }}>
                      <div>
                        <div style={{
                          color: '#a0a9c1',
                          fontFamily: 'Georgia, serif'
                        }}>Entry</div>
                        <div style={{
                          color: '#ffffff',
                          fontFamily: 'Georgia, serif'
                        }}>{formatCurrency(position.entry)}</div>
                      </div>
                      <div>
                        <div style={{
                          color: '#a0a9c1',
                          fontFamily: 'Georgia, serif'
                        }}>Current</div>
                        <div style={{
                          color: '#ffffff',
                          fontFamily: 'Georgia, serif'
                        }}>{formatCurrency(position.current)}</div>
                      </div>
                      <div>
                        <div style={{
                          color: '#a0a9c1',
                          fontFamily: 'Georgia, serif'
                        }}>P&L %</div>
                        <div style={{
                          color: position.pnlPercent > 0 ? '#00FF5F' : '#FF0000',
                          fontFamily: 'Georgia, serif'
                        }}>
                          {formatPercent(position.pnlPercent)}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#a0a9c1',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {position.strategy} • Day {position.daysHeld} • Stop: {formatCurrency(position.stopLoss)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* PROPERLY CONTAINED CHARTS SECTION - FIXED! */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '24px'
          }}>
            {/* Monthly P&L - PROPERLY CONTAINED */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                fontFamily: 'Georgia, serif'
              }}>Monthly Net P&L</h3>
              <div style={{
                width: '100%',
                height: '280px',
                padding: '0'
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={monthlyPnL}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#3a4a6b" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#a0a9c1" 
                      style={{ fontFamily: 'Georgia, serif', fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="#a0a9c1" 
                      style={{ fontFamily: 'Georgia, serif', fontSize: '12px' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#2B3554',
                        border: '1px solid #3a4a6b',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontFamily: 'Georgia, serif'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="pnl" 
                      stroke="#00FF5F" 
                      fill="#00FF5F" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Profit by Region - PROPERLY CONTAINED */}
            <div style={{
              backgroundColor: '#202A44',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #3a4a6b',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                fontFamily: 'Georgia, serif'
              }}>Profit by Region</h3>
              <div style={{
                width: '100%',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={profitByRegion}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                      startAngle={90}
                      endAngle={450}
                    >
                      {profitByRegion.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#2B3554',
                        border: '1px solid #3a4a6b',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontFamily: 'Georgia, serif'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginTop: '16px',
                flexWrap: 'wrap'
              }}>
                {profitByRegion.map((region, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: region.color
                    }}></div>
                    <div style={{
                      color: '#a0a9c1',
                      fontSize: '14px',
                      fontFamily: 'Georgia, serif'
                    }}>{region.name}</div>
                    <div style={{
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'Georgia, serif'
                    }}>{region.value}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;